import {Component, forwardRef, Input, ChangeDetectionStrategy} from '@angular/core';
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {AbstractValueAccessorComponent} from "../component/value-accessor.component";
import moment from "moment/moment";
import {unitOfTime} from "moment";

type AllowedUnits = Extract<unitOfTime.Base, 'second' | 'minute' | 'hour' | 'day'>;
const allowedUnits: AllowedUnits[] = ['second', 'minute', 'hour', 'day'];

@Component({
  selector: 'app-duration',
  templateUrl: './duration.component.html',
  styleUrl: './duration.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DurationComponent), multi: true }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DurationComponent extends AbstractValueAccessorComponent<string> {
  @Input() required;
  @Input() readonly: boolean = false;

  @Input() outputMode: 'seconds' | 'iso' = 'iso';
  @Input() defaultUnit: AllowedUnits = 'hour';
  @Input() units: AllowedUnits[] = allowedUnits;

  protected amount = 0;
  protected unit: AllowedUnits = 'second';

  private valueSeconds = 0;

  get value(): any {
    return this.outputMode === 'iso'
      ? moment.duration(this.valueSeconds, 'second').toISOString() : this.valueSeconds;
  }

  writeValue(value: string | number): void {
    this.valueSeconds = this.parseToSeconds(value);
    this.syncUIFromSeconds(this.valueSeconds);
  }

  private parseToSeconds(value: number | string | null | undefined): number {
    const duration = DurationComponent.toDuration(value);
    const secs = duration.asSeconds();
    return Number.isFinite(secs) && secs >= 0 ? Math.round(secs) : 0;
  }

  private syncUIFromSeconds(totalSeconds: number) {
    const picked = this.pickLargestWholeUnit(totalSeconds);
    this.unit = picked.unit;
    this.amount = picked.amount;
  }

  onAmountInput(raw: string) {
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) {
      return;
    }
    this.amount = num;
    this.updateFromUI();
  }

  onUnitChange(unit: AllowedUnits) {
    this.unit = unit;
    this.updateFromUI();
  }

  private updateFromUI() {
    this.valueSeconds = this.fromUnit(this.amount, this.unit);
    this.onModelChange();
  }

  private toUnit(secs: number, unit: AllowedUnits): number {
    return secs / DurationComponent.getDivisor(unit);
  }

  private fromUnit(amount: number, unit: AllowedUnits): number {
    return Math.max(0, Math.round(amount * DurationComponent.getDivisor(unit)));
  }

  private pickLargestWholeUnit(secs: number): { unit: AllowedUnits; amount: number } {
    if (secs === 0) {
      return {
        unit: this.defaultUnit,
        amount: 0
      }
    }
    const order: AllowedUnits[] = this.units.slice().reverse();
    for (const u of order) {
      if (!this.units.includes(u)) {
        continue;
      }
      const divisor = DurationComponent.getDivisor(u);
      if (secs % divisor === 0) {
        return { unit: u, amount: secs / divisor };
      }
    }
    const fallback: AllowedUnits = this.units.includes('second') ? 'second' : this.units[0];
    return {
      unit: fallback,
      amount: Math.round(this.toUnit(secs, fallback))
    };
  }

  private static getDivisor(unit: AllowedUnits): number {
    switch (unit) {
      case "day": return 86400;
      case "hour": return 3600;
      case 'minute': return 60;
      case 'second': return 1;
    }
  }

  static toDuration(value: number | string): moment.Duration {
    if (value == null) {
      return moment.duration(0, 'second');
    }
    if (typeof value === 'number') {
      return moment.duration(Math.max(0, Math.floor(value)), 'second');
    }
    const trimmed = String(value).trim();
    const maybeNum = Number(trimmed);
    if (!Number.isNaN(maybeNum)) {
      return moment.duration(Math.max(0, Math.floor(maybeNum)), 'second');
    }
    return moment.duration(trimmed as any);
  }

  static durationToReadableString(value: number | string | moment.Duration): string {
    const duration = moment.isDuration(value) ? (value as moment.Duration)
      : DurationComponent.toDuration(value as number | string);

    if (typeof duration.isValid === 'function' && !duration.isValid()) {
      return "";
    }
    const totalSeconds = duration.asSeconds();
    if (totalSeconds === 0) {
      return '0 seconds';
    }
    let remaining = Math.floor(Math.abs(totalSeconds));
    const parts: string[] = [];
    for (const unit of allowedUnits.slice().reverse()) {
      const qty = Math.floor(remaining / this.getDivisor(unit));
      if (qty > 0) {
        parts.push(`${qty} ${unit}${qty === 1 ? '' : 's'}`);
        remaining %= this.getDivisor(unit);
      }
    }
    return parts.join(' ');
  }
}
